import { useId } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { LoadingScreen } from "../lib/Loading.tsx";
import { AddHistoryDialog } from "../provenance/AddHistoryDialog.tsx";
import { ProvenanceEntryList } from "../provenance/ProvenanceEntryList.tsx";
import { PROVENANCE_COPY } from "../provenance/provenance.constants.ts";
import { FOCUS_PARAMS } from "../routes/routes.constants.ts";
import { withParam } from "../routes/routes.model.ts";
import { Spotlight } from "../spotlight/Spotlight.tsx";
import { AssignAgentDialog } from "./AssignAgentDialog.tsx";
import { ConsignPanel } from "../consign/ConsignPanel.tsx";
import { OwnerSalePanel } from "../sell/OwnerSalePanel.tsx";
import { InsurancePanel } from "../insurance/InsurancePanel.tsx";
import { LocationPanel } from "../location/LocationPanel.tsx";
import { WorkAgentsPanel } from "./WorkAgentsPanel.tsx";
import { WorkEstatePanel } from "./WorkEstatePanel.tsx";
import { WorkDisclosurePanel } from "./WorkDisclosurePanel.tsx";
import { WorkAttributionVisibilityPanel } from "./WorkAttributionVisibilityPanel.tsx";
import { WorkCorrectionPanel } from "./WorkCorrectionPanel.tsx";
import { WorkPlacementPanel } from "./WorkPlacementPanel.tsx";
import { WorkRepresentationPanel } from "./WorkRepresentationPanel.tsx";
import { WorkVisibilityPanel } from "./WorkVisibilityPanel.tsx";
import { WitnessPanel } from "../witness/WitnessPanel.tsx";
import { WorkAttributionPanel } from "./WorkAttributionPanel.tsx";
import { WorkCode } from "./WorkCode.tsx";
import { WorkIdentity } from "./WorkIdentity.tsx";
import { WorkImages } from "./WorkImages.tsx";
import { NotYetPanel, WorkTabs } from "./WorkTabs.tsx";
import {
  WORK_ASIDE_NOT_YET,
  WORK_RECORD_COPY,
} from "./work.constants.ts";
import { parseTab, tabParam } from "./work.model.ts";
import type { WorkPanel } from "./work.model.ts";
import { useWorkController } from "./useWorkController.ts";
import type { WorkController } from "./useWorkController.ts";
import "../provenance/provenance.css";
import "./work.css";

/**
 * One work, and what the registry can say about it.
 *
 * Two columns, after the prototype: the object on the left — its photographs
 * and its catalogue facts — and everything claimed about it on the right, under
 * tabs. Only Provenance is built. The other five tabs, and the three panels
 * under the facts, render what they are waiting for rather than being left out,
 * so the screen doubles as the map of what is still to build.
 *
 * NO SHARE CONTROL EVER APPEARS ON THIS SCREEN. Showing one piece to a valuer is
 * a deliberate grant — see the comment in useCollectorController.
 */
export function WorkRecord({
  workId,
  collectionHref,
  canAssignAgent,
  canWriteHistory = true,
  canConsign = false,
  canSell = false,
  actingAs = null,
}: {
  workId: string;
  collectionHref: string;
  /**
   * `capabilities.assignAgent`, from the workspace.
   *
   * A prop rather than a second `useMe` here: the shell already holds the
   * capabilities and passing them down keeps one answer on the screen. Never
   * `if (featureNotBuilt)` — the server decides, and it says false for a caller
   * who owns no work.
   */
  canAssignAgent: boolean;
  /**
   * The caller's rung, where the record is reached through a FIRM.
   *
   * `ondo.provenance_writable_work_ids()` unions
   * `agented_curatable_work_ids()`, which is editor and above — so a viewer at
   * an advisory could open a client's record, see "+ Add history", and be
   * refused on submit. Unlike `canAssignAgent` this cannot come from
   * `my_capabilities()`: that answers about the ACCOUNT, and the rung is about
   * the seat the record was reached through.
   *
   * Defaults true, which is the personal-workspace answer — a collector on
   * their own work has no rung, and ownership decides instead.
   */
  canWriteHistory?: boolean;
  /**
   * `capabilities.consignWork`, from the workspace — the same account-level
   * hint `canAssignAgent` is, and gated the same way: it draws the control,
   * and `ondo.offer_consignment()` decides whether THIS work may be offered.
   * False by default so a portal that has not thought about it draws nothing.
   */
  canConsign?: boolean;
  /**
   * `capabilities.transferWork`, from the workspace — the OWNER's own sale,
   * OND-173.
   *
   * The same account-level hint `canConsign` and `canAssignAgent` are, and
   * gated the same way: it draws the control, and `ondo.effect_owner_sale()`
   * decides whether THIS work may be sold — a work consigned to a gallery, one
   * awaiting title from an earlier sale and one locked by a covenant are all
   * its refusals to make.
   *
   * FALSE BY DEFAULT, so a portal that has not thought about it draws nothing.
   * The collector's workspace passes it; the gallery sells from its inventory
   * under a consignment instead, which is a different act with a different
   * door, and an agent is refused outright — 20261014093000 decision 12, "an
   * agent may not dispose of a work".
   */
  canSell?: boolean;
  /**
   * Which of the reader's own firms a read of this record is recorded against —
   * OND-68.
   *
   * Threaded from the workspace rather than worked out here, for the reason
   * `useWorkController` gives: only the workspace knows which seat is open.
   * Null is a real value — a reader acting as themselves — and it changes
   * nothing about what the record shows.
   */
  actingAs?: string | null;
}) {
  // `?entry=` and `?tab=`, and the only place either lives — the controller
  // holds no copy of the tab, so Back, a reload and a pasted link cannot
  // disagree with the screen. See `FOCUS_PARAMS`.
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const focusEntryId = params.get(FOCUS_PARAMS.entry);
  const namedTab = params.get(FOCUS_PARAMS.tab);
  const controller = useWorkController({
    workId,
    actingAs: actingAs ?? null,
    focusEntryId,
    // OND-22. Once the work has left its collection the reader may no longer be
    // able to open it, so the screen goes back to where they came from — the
    // same address the Back link below uses, which is the one this component
    // was handed and the hook was deliberately not.
    onRemoved: () => void navigate(collectionHref),
    // A LINK BEATS A TAB THAT WAS NEVER CHOSEN. A notification carries `?entry=`
    // and no `?tab=` — see `workPath` — and landing that on a filtering tab
    // would open a panel that hides the very row the link is about. An unnamed
    // tab is the default tab, which is the history, so the rule holds through
    // `parseTab` alone; the argument for it lives there now, beside the
    // fallback it depends on. Once the reader picks a tab it is named and it
    // wins: deferring for as long as `?entry=` sat in the address would leave
    // the strip unable to move.
    tab: parseTab(namedTab),
    canAssignAgent,
    // Threaded in so the attribution panel's ask is decided in the model rather
    // than by this file combining a prop with the model's answer.
    canWriteHistory,
    onSelectTab: (next) => {
      // `replace`, because a tab is a filter over one record and not a journey:
      // six glances at six tabs must not put six entries in the history between
      // the reader and where they came from. The updater form rather than the
      // captured `params`, so a tab pressed twice quickly does not write the
      // second change over a stale copy of the first.
      setParams((current) => withParam(current, FOCUS_PARAMS.tab, tabParam(next)), {
        replace: true,
      });
    },
  });
  const { view } = controller;
  const panelId = useId();

  if (view.kind === "loading") return <LoadingScreen message={view.message} />;

  if (view.kind === "not-found" || view.kind === "error") {
    return (
      <section className="work">
        <p className="collection__note">{view.message}</p>
        <Link to={collectionHref} className="work__back">
          {WORK_RECORD_COPY.backToCollection}
        </Link>
      </section>
    );
  }

  return (
    <section className="work">
      <div className="work__columns">
        <div className="work__object">
          <WorkImages images={view.images} onSelect={controller.selectImage} />

          <WorkIdentity identity={view.identity} />

          {/* Directly under the catalogue facts and above the code, because it
              is the question every other claim on the record hangs off: a chain
              of title is a chain of title to something, and whose work that
              something is, is the first thing a collector asks. OND-20. */}
          <WorkAttributionPanel
            panel={view.attribution}
            onAsk={controller.askAttribution}
            onOpenEntry={controller.openEntry}
          />

          {/* Above the agent panel, because it is what makes that panel's other
              half possible: this is the string a collector hands over so an
              agent can ask, and the panel below is where the answer lands. */}
          <WorkCode code={view.ondoCode} facts={view.codeFacts} />

          {/* HOW FAR THIS WORK TRAVELS — OND-47. FIRST of the three panels
              about who reaches the record, and the order is the argument: this
              one sets the ceiling, the estate panel below is the one audience
              that sits outside it, and the agents panel after that says who
              may ACT rather than who may read. A reader who met the estate
              exception before meeting the setting it is an exception to would
              have to read upwards. It draws nothing for anybody but the
              owner. */}
          <WorkVisibilityPanel
            panel={view.visibilityPanel}
            busy={controller.visibility.busy}
            failed={controller.visibility.failed}
            onChoose={controller.visibility.onChoose}
            onChooseLocationPublic={controller.visibility.onChooseLocationPublic}
          />

          {/* THE ESTATE'S HALF OF THE PUBLIC VIEW — OND-172. Beside the panel
              above rather than inside it, because it is a different PARTY's
              control and not a different act of the same party's: the artist and
              the year are the estate's to withhold, everything else on a public
              view is the owner's, and neither can reach the other's field.

              NOBODY EVER SEES BOTH. The server answers one of the two null for
              each caller, so an owner reads the panel above and an estate reads
              this one — which is why they can sit adjacent in the markup without
              a reader ever having to work out which half is theirs. */}
          <WorkAttributionVisibilityPanel
            panel={view.attributionVisibilityPanel}
            busy={controller.attributionVisibility.busy}
            failed={controller.attributionVisibility.failed}
            onChoose={controller.attributionVisibility.onChoose}
          />

          {/* WHO SEES WHICH LINE — OND-79. SECOND, directly under the ceiling
              it filters: that panel says how far the work travels and this one
              says, of the people it reaches, who sees which part of its
              history. A reader meeting the filters before the ceiling would
              not know what audience they are filtering.

              It draws nothing for a caller who is neither the owner nor an
              agent on the work, because `GET /disclosure` refuses them — so
              there is no panel rather than a panel drawn empty. */}
          <WorkDisclosurePanel
            panel={controller.disclosure.panel}
            draft={controller.disclosure.draft}
            entries={controller.disclosure.entries}
            busy={controller.disclosure.busy}
            failed={controller.disclosure.failed}
            handlers={controller.disclosure.handlers}
          />

          {/* WHAT THE ARTIST'S ESTATE CAN SEE — OND-58. Directly under the
              code and above the agents, because it belongs with the other
              question about who else reaches this record: the panel below says
              who may ACT on the work, this one says who may READ it. It draws
              nothing for anybody but the owner. */}
          <WorkEstatePanel
            panel={view.estatePanel}
            busy={controller.estate.busy}
            failed={controller.estate.failed}
            onSetHidden={controller.estate.onSetHidden}
            onSetRecordOpen={controller.estate.onSetRecordOpen}
          />

          <WorkAgentsPanel
            panel={view.agentPanel}
            busy={view.busy}
            onAssign={controller.assignAgent}
            onEnd={controller.agents.onEnd}
            onRenew={controller.agents.onRenew}
            onSetScope={controller.agents.onSetScope}
          />

          {/* THE SAME QUESTION FOR EVERYBODY ELSE — OND-73. The panel above is
              the owner's, with its controls; this one is what a viewer the work
              reaches — a buyer let in by a grant, a consignee, a borrower, a
              co-agent — reads about who represents it, with the ended and
              expired under their own heading so they never read as current.
              The model hides exactly one of the two for every caller, so this
              never draws beside a populated agents panel. */}
          <WorkRepresentationPanel panel={view.representationPanel} />

          {/* Beside the agents panel because they are the same kind of thing:
              who acts on this work besides its owner. The gallery's inventory
              has said since it shipped that "an artist or estate consigns a
              work from its own record" — this is that record. */}
          <ConsignPanel workId={workId} canAct={canConsign} />

          {/* THE OWNER SELLING IT THEMSELVES — OND-173. Directly under the
              consignment panel, because the two are the only two ways title
              leaves this record and the engine makes them exclusive: a work
              under a live accepted consignment is the gallery's to sell, so
              this panel draws the gallery's sentence instead of a control. The
              panel above is who may sell on your behalf; this is you.

              `onSold` RE-READS rather than patching. A sale writes a transfer,
              a line of provenance and — for an off-ONDO buyer — settlement
              rows, and hands the rest to `saleConsequences`; nothing here could
              guess which have landed. */}
          <OwnerSalePanel
            workId={workId}
            subject={`${view.identity.title} · ${view.identity.attribution} · ${view.ondoCode}`}
            canSell={canSell}
            onSold={controller.reload}
          />

          {/* Where it is, and — for its owner — the one place to say so. Below
              the arrangements because possession is not representation: the
              row above says who may act on the work, this one says where the
              object sits. The record's own Location line stays in the identity
              block for every reader; this panel is what changes it. */}
          <LocationPanel
            workId={workId}
            initial={view.location}
            canAct={view.canSetLocation}
            onRecorded={controller.applyLocation}
          />

          {/* Which of the owner's collections files it — OND-22. Directly under
              the location because the two are the same sort of fact about the
              object in the owner's hands: where it physically is, and where it
              is filed. Nothing for anybody but the owner of the holding
              collection, which is the rung both doors ask for. */}
          <WorkPlacementPanel
            panel={view.placementPanel}
            busy={controller.placement.busy}
            failed={controller.placement.failed}
            onMove={controller.placement.onMove}
            onArmRemove={controller.placement.onArmRemove}
            onCancelRemove={controller.placement.onCancelRemove}
            onRemove={controller.placement.onRemove}
          />

          {/* A mistyped catalogue field, corrected as a second line — OND-41.
              Under the placement because both are the owner's housekeeping of
              their own entry; drawn for the attributed estate too, which is the
              other party the door admits. The identity block above is where the
              correction shows: the value marked CORRECTED, with what the record
              first said beside it, for ever. */}
          <WorkCorrectionPanel
            panel={view.correctionPanel}
            busy={controller.correction.busy}
            failed={controller.correction.failed}
            onChooseField={controller.correction.onChooseField}
            onChangeValue={controller.correction.onChangeValue}
            onSubmit={controller.correction.onSubmit}
          />

          {/* Who insures it — OND-48, derived from the history rather than from
              a field of its own. Below the location because the two answer the
              same sort of question about the object and a reader looking for
              one will scan for the other; above the planned panels because this
              one is answerable. Hidden entirely when no period of cover stands.

              `onOpenEntry` sets `?entry=` on this very page, which is why the
              row is a button and not a link: ?entry= outranks ?tab=, so it
              opens the tab holding every kind and lands on the period — where
              the dispute's reason and the recorded responses are. */}
          <InsurancePanel
            panel={view.insurancePanel}
            onOpenEntry={(entryId) => {
              setParams((current) => withParam(current, FOCUS_PARAMS.entry, entryId), {
                replace: true,
              });
            }}
          />

          {Object.entries(WORK_ASIDE_NOT_YET).map(([key, copy]) => (
            <NotYetPanel
              key={key}
              title={copy.title}
              lead={copy.lead}
              detail={copy.detail}
            />
          ))}
        </div>

        <div className="work__claims">
          <WorkTabs
            tabs={view.tabs}
            panelId={panelId}
            onSelect={controller.selectTab}
            action={
              view.panel.kind === "provenance" ? (
                // DISABLED WITH A REASON rather than hidden, which is the rule
                // the Sell control follows: the entry point to a work's history
                // is the record's own subject, and a reader who cannot find it
                // has no way to tell a missing feature from a junior seat.
                <button
                  type="button"
                  className="work__action"
                  disabled={view.full || view.busy || !canWriteHistory}
                  title={
                    !canWriteHistory
                      ? WORK_RECORD_COPY.historyNeedsEditor
                      : view.full
                        ? PROVENANCE_COPY.tooManyEntries
                        : undefined
                  }
                  onClick={controller.addProvenanceEntry}
                >
                  {WORK_RECORD_COPY.addEntry}
                </button>
              ) : undefined
            }
          />

          {/* Named by the open tab and focusable, so arrowing to a tab and then
              tabbing forward lands in what it opened. The tab ids existed and
              were referenced by nothing. */}
          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={`work-tab-${view.activeTab}`}
            tabIndex={0}
            className="work__panel"
          >
            {/* Two arms and no third, since OND-38. There was a `not-yet`
                panel here for the one tab the registry could not answer; the
                citation is the Significant publications tab now, so every tab
                on this record answers with the record. `NotYetPanel` is still
                drawn above, for the ASIDE panels that are waiting on
                something. */}
            {view.panel.kind === "provenance" ? (
              <Provenance view={view} panel={view.panel} controller={controller} />
            ) : (
              <WitnessPanel workId={workId} />
            )}
          </div>
        </div>
      </div>

      <Link to={collectionHref} className="work__back">
        {WORK_RECORD_COPY.backToCollection}
      </Link>

      {/* One viewer for the screen. It renders nothing until a document is
          opened, so nothing about it is on the page the rest of the time. */}
      <Spotlight view={controller.spotlight} onClose={controller.closeDocument} />

      {/* Mounted even when shut — closing by unmounting would skip close(), and
          it is close() that returns focus to "+ Add history". */}
      <AddHistoryDialog
        view={view.dialog}
        onChooseKind={controller.history.onChooseKind}
        onSetField={controller.history.onSetField}
        onAddDocuments={controller.history.onAddDocuments}
        onSetDocumentKind={controller.history.onSetDocumentKind}
        onOpenDocument={controller.history.onOpenDocument}
        onRemoveDocument={controller.history.onRemoveDocument}
        onAttachOndoId={controller.history.onAttachOndoId}
        onDetachOndoId={controller.history.onDetachOndoId}
        onAct={controller.history.onAct}
        onUnact={controller.history.onUnact}
        onSubmit={controller.history.onSubmit}
        onCancel={controller.history.onCancel}
        onClose={controller.history.onClose}
      />

      {/* Mounted even when shut, beside the other two, for the reason the dialog
          itself states: closing by unmounting skips close(), and close() is what
          returns focus to the button that opened it. */}
      <AssignAgentDialog
        view={view.assignAgent}
        onSetOndoId={controller.agents.onSetOndoId}
        onSetNote={controller.agents.onSetNote}
        onSetExpiresOn={controller.agents.onSetExpiresOn}
        onSetMayDelegate={controller.agents.onSetMayDelegate}
        onSetOrganisationOndoId={controller.agents.onSetOrganisationOndoId}
        onSetMayConsign={controller.agents.onSetMayConsign}
        onSetTerm={controller.agents.onSetTerm}
        onCheck={controller.agents.onCheck}
        onSubmit={controller.agents.onSubmit}
        onCancel={controller.agents.onCancel}
        onClose={controller.agents.onClose}
      />
    </section>
  );
}

/** The one built tab: the chain of title, and what can be done to it. */
function Provenance({
  view,
  panel,
  controller,
}: {
  view: Extract<WorkController["view"], { kind: "open" }>;
  panel: Extract<WorkPanel, { kind: "provenance" }>;
  controller: WorkController;
}) {
  return (
    <div className="work__provenance provenance">
      <h3 className="provenance__title">{panel.title}</h3>
      <p className="provenance__help">{panel.help}</p>

      {view.error !== null && (
        <p className="provenance__problem" role="alert">
          {view.error}
        </p>
      )}

      {/* An empty tab here is a statement about the RECORD — nobody has
          recorded one of these — and says which chip records the first. Every
          tab has its own sentence, so nothing falls through to the list's
          four-word line, which belongs to the register wizard. */}
      {view.rows.length === 0 ? (
        <p className="provenance__empty">{panel.empty}</p>
      ) : (
        <ProvenanceEntryList
          rows={view.rows}
          openEntryId={view.openEntryId}
          refusals={view.refusals}
          handlers={controller.provenance}
        />
      )}
    </div>
  );
}
